import React, { useState } from 'react';
import { FileText, Instagram, Youtube, Settings, Loader2 } from 'lucide-react';
import { useStore } from '../lib/store';
import { formatContent } from '../lib/teachingService';
import { PromptEditor } from './PromptEditor';

export const ContentFormatter = () => {
  const teachings = useStore((state) => state.teachings);
  const [loading, setLoading] = useState<string | null>(null);
  const [formattedContent, setFormattedContent] = useState<Record<string, string>>({});
  const [showPromptEditor, setShowPromptEditor] = useState<'reel' | 'long' | 'short' | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  if (teachings.length === 0) {
    return null;
  }

  const latestTeaching = teachings[teachings.length - 1];

  const handleFormat = async (type: 'reel' | 'long' | 'short') => {
    try {
      setLoading(type);
      setError(null);

      const allContent = [
        latestTeaching.content,
        ...(latestTeaching.additionalTranscripts?.map(t => t.content) || [])
      ].join('\n\n');

      const content = await formatContent(allContent, type);
      setFormattedContent(prev => ({
        ...prev,
        [type]: content
      }));
    } catch (error) {
      console.error('Error formatting content:', error);
      setError('Failed to generate content. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const formatButtons = [
    {
      type: 'reel' as const,
      icon: Instagram,
      label: 'Reel Script',
      title: 'Instagram/TikTok Reel'
    },
    {
      type: 'long' as const,
      icon: FileText,
      label: 'Long Post',
      title: 'Long-form Social Post'
    },
    {
      type: 'short' as const,
      icon: Youtube,
      label: 'Short Form',
      title: 'Short-form Content'
    }
  ];

  return (
    <div className="mt-8 w-full max-w-4xl mx-auto p-6 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800">
      <h2 className="text-2xl font-bold text-amber-500 mb-6">Format Content</h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {formatButtons.map(({ type, icon: Icon, label, title }) => (
          <div key={type} className="relative">
            <button 
              onClick={() => handleFormat(type)}
              disabled={loading === type}
              className="w-full flex items-center justify-center gap-2 p-4 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors disabled:opacity-50 relative overflow-hidden"
            >
              {loading === type && (
                <div className="absolute inset-0 bg-amber-500/10 animate-pulse" />
              )}
              {loading === type ? (
                <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              ) : (
                <Icon className="w-5 h-5 text-amber-500" />
              )}
              <span className="text-gray-100">
                {loading === type ? 'Generating...' : label}
              </span>
            </button>
            <button
              onClick={() => setShowPromptEditor(type)}
              className="absolute -right-2 -top-2 p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
              title="Edit Prompt"
            >
              <Settings className="w-4 h-4 text-amber-500" />
            </button>
          </div>
        ))}
      </div>
      
      {showPromptEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="w-full max-w-2xl bg-gray-900 rounded-lg p-6">
            <PromptEditor
              type={showPromptEditor}
              title={formatButtons.find(b => b.type === showPromptEditor)?.title || ''}
              onClose={() => setShowPromptEditor(null)}
            />
          </div>
        </div>
      )}
      
      {Object.entries(formattedContent).map(([type, content]) => (
        <div key={type} className="mt-6">
          <h3 className="text-lg font-semibold text-amber-500 mb-2">
            {formatButtons.find(b => b.type === type)?.title}
          </h3>
          <div className="p-4 rounded-lg bg-gray-950">
            <pre className="whitespace-pre-wrap text-gray-100">{content}</pre>
          </div>
        </div>
      ))}
    </div>
  );
};